import { coverageFieldKeys } from './coverage.js';
import { defaultAddressHeadingFieldEntry, defaultDiscountCodeFieldEntry, defaultFormConfig, defaultNewsletterFieldEntry, defaultOrderSummaryFieldEntry, defaultQuantityOffersFieldEntry, defaultShippingMethodFieldEntry, defaultSubmitFieldEntry, defaultTermsFieldEntry, defaultTotalsSummaryFieldEntry, } from './defaults.js';
import { DEFAULT_ADDRESS_HEADING_TEXT, defaultCoverage, defaultFieldsStyle, defaultHeader, defaultMessages, defaultProductCard, defaultShippingMethod, defaultThankYou, defaultTotalsStyle, defaultTracking, formConfig, } from './schema.js';
/**
 * Ensure the config carries every "block" field (order summary, discount code, totals,
 * shipping method and quantity offers). A normalized config should always have them; this is a
 * defensive net (e.g. the best-effort fallback below) that prepends any that are missing —
 * readers can then render and reorder them like any other field. Missing entries are prepended
 * in reverse render order so the final order matches the default layout. A config that already
 * has an entry is untouched.
 *
 * 🚨 **The address heading is deliberately NOT backfilled.** It is a `custom_text` field now, and
 * a merchant is allowed to delete it — backfilling would put it straight back on their next save.
 * The existence check here is by TYPE, so it could not tell that field from any other custom text
 * block anyway. Stored `address_heading` blocks are converted by `migrateAddressHeadingField`.
 */
function withBlockFields(config) {
    const existing = Array.isArray(config.fields) ? config.fields : [];
    let fields = existing;
    const prepend = (entry) => {
        if (!fields.some((f) => f.type === entry.type))
            fields = [entry, ...fields];
    };
    const append = (entry) => {
        if (!fields.some((f) => f.type === entry.type))
            fields = [...fields, entry];
    };
    // Reverse render order: the last prepended ends up first.
    prepend({ ...defaultQuantityOffersFieldEntry });
    prepend({ ...defaultShippingMethodFieldEntry });
    prepend({ ...defaultTotalsSummaryFieldEntry });
    prepend({ ...defaultDiscountCodeFieldEntry });
    prepend({ ...defaultOrderSummaryFieldEntry });
    // Form-chrome blocks belong at the end (submit last), matching the historical bottom layout.
    append({ ...defaultNewsletterFieldEntry });
    append({ ...defaultTermsFieldEntry });
    append({ ...defaultSubmitFieldEntry });
    return fields === existing ? config : { ...config, fields };
}
/**
 * Bring an old `consent` shape up to the current one so a config saved before consent was
 * remodeled still parses: the newsletter opt-in used to be a bare boolean (now `{ text, style }`)
 * and terms carried an `enabled` flag (now governed by the `terms` field entry). Returns the
 * input untouched when it already looks current.
 */
function migrateConsent(input) {
    const consent = input.consent;
    if (!consent || typeof consent !== 'object')
        return input;
    const c = consent;
    if (typeof c.newsletter !== 'boolean')
        return input; // already the new object shape
    const nl = defaultFormConfig.consent.newsletter;
    return {
        ...input,
        consent: {
            ...c,
            // Preserve the on/off intent by seeding the field via defaults; text/style come from defaults.
            newsletter: { text: nl.text, style: { ...nl.style } },
        },
    };
}
/**
 * Convert a stored `address_heading` block into the `custom_text` field that replaced it.
 *
 * 🚨 **Converts, never drops.** The block's whole config was one string the merchant may well have
 * rewritten ("Ingresá tu dirección", "Onde entregamos?"), and its position in `fields` is theirs
 * too. Deleting the entry the way `migrateHeaderField` deletes a stale header would silently take
 * a line of their form away; this keeps the words, the position and the enabled flag, and hands
 * them a block they can now restyle and delete.
 *
 * Runs PRE-PARSE, like the header migration and for the same reason: `address_heading` is no longer
 * in the field enum, so a stored entry would fail validation and drop the whole config into
 * `normalizeFormConfig`'s degraded branch.
 *
 * The text comes from the top-level `address_heading.text` — the key zod is about to strip — with
 * the shipped wording as the fallback. Anything else on the stored entry (`enabled`, and any keys a
 * later version added) is carried through.
 */
function migrateAddressHeadingField(input) {
    if (!Array.isArray(input.fields))
        return input;
    const isHeading = (f) => Boolean(f) &&
        typeof f === 'object' &&
        f.type === 'address_heading';
    if (!input.fields.some(isHeading))
        return input;
    const stored = input.address_heading;
    const text = stored && typeof stored === 'object' && typeof stored.text === 'string'
        ? stored.text
        : DEFAULT_ADDRESS_HEADING_TEXT;
    const fields = input.fields.map((f) => isHeading(f)
        ? {
            ...f,
            ...defaultAddressHeadingFieldEntry,
            // The merchant's own on/off choice survives; everything else comes from the new entry.
            enabled: typeof f.enabled === 'boolean' ? f.enabled : true,
            text_block: { ...defaultAddressHeadingFieldEntry.text_block, content: text },
        }
        : f);
    return { ...input, fields };
}
/**
 * Drop the obsolete `header` field entry. The header was briefly modeled as a reorderable field;
 * it's now fixed chrome carried by the top-level `header` key. A persisted header field is stale —
 * its type is no longer in the field enum, so it would fail validation (and block saves) — so we
 * strip it pre-parse, letting the rest of the config validate and backfill cleanly. Returns the
 * input untouched when there's no such entry.
 */
function migrateHeaderField(input) {
    if (!Array.isArray(input.fields))
        return input;
    const fields = input.fields.filter((f) => !(f && typeof f === 'object' && f.type === 'header'));
    return fields.length === input.fields.length ? input : { ...input, fields };
}
/**
 * Force a configured coverage's chain fields visible, and keep `required` consistent down it.
 *
 * 🚨 A coverage's fields are not the merchant's to hide — see `coverageFieldKeys` for the three
 * ways it breaks. This is the same kind of invariant `withBlockFields` above enforces, applied at
 * the same chokepoint: `normalizeFormConfig` is what both the admin read and the served public
 * config go through, so one call covers the Designer, its preview and BOTH storefront bundles
 * without any of them having to remember.
 *
 * Two rules, and the second is narrower than it looks:
 *
 *  - **Visible.** Every chain field gets `enabled: true`. Non-negotiable: without it the cascade
 *    below it has nothing to walk.
 *  - **Required propagates UPWARD, never downward.** If the merchant made `city` required, then
 *    `province` must be too — otherwise a buyer can skip province, find city's dropdown empty, and
 *    be blocked by a required field they cannot fill. The reverse is left alone: a merchant who
 *    requires nothing gets a coverage that only gates the levels buyers actually choose, which is
 *    exactly what `isWithinCoverage`'s short-circuit already documents. We enforce what the
 *    cascade structurally needs, not what we would have chosen for them.
 *
 * Returns the input untouched when no coverage is configured, so the common case allocates nothing.
 *
 * Exported because the Designer applies it to its DRAFT the moment the merchant picks a coverage.
 * Without that the draft — and therefore the live preview — kept showing a chain field hidden
 * until a save-and-reload round trip put it back, so the preview disagreed with the storefront for
 * exactly as long as the merchant was looking at it.
 */
export function withCoverageFields(config) {
    const chain = coverageFieldKeys(config.coverage);
    if (chain.length === 0)
        return config;
    const fields = Array.isArray(config.fields) ? config.fields : [];
    // Walk the chain from the DEEPEST required level upward: everything above it is required too.
    const deepestRequired = chain.reduce((deepest, key, index) => (fields.some((f) => f.key === key && f.required) ? index : deepest), -1);
    let changed = false;
    const next = fields.map((f) => {
        const index = chain.indexOf(f.key);
        if (index === -1)
            return f;
        const required = f.required || index < deepestRequired;
        if (f.enabled && f.required === required)
            return f;
        changed = true;
        return { ...f, enabled: true, required };
    });
    return changed ? { ...config, fields: next } : config;
}
function flatten(error) {
    return error.issues.map((i) => ({
        path: i.path.join('.'),
        message: i.message,
    }));
}
/** Validate an unknown payload against the form config schema. Never throws. */
export function validateFormConfig(input) {
    const parsed = formConfig.safeParse(input);
    if (parsed.success) {
        return { ok: true, config: parsed.data };
    }
    return { ok: false, errors: flatten(parsed.error) };
}
/** Validate and throw on failure (use where invalid input is a programmer error). */
export function parseFormConfig(input) {
    return formConfig.parse(input);
}
/**
 * Normalize a stored config for serving: applies schema defaults so configs saved before
 * a key existed (e.g. fields_style) are backfilled. If the stored config does not fully
 * validate, falls back to a best-effort backfill of the new keys so readers never crash.
 */
export function normalizeFormConfig(input) {
    // Run pre-parse migrations (legacy consent shape, obsolete header field, retired
    // `address_heading` block) so the config validates cleanly instead of falling back to the
    // best-effort branch below.
    const migrated = input && typeof input === 'object'
        ? migrateAddressHeadingField(migrateHeaderField(migrateConsent(input)))
        : input;
    const parsed = formConfig.safeParse(migrated);
    if (parsed.success)
        return withCoverageFields(withBlockFields(parsed.data));
    const obj = migrated && typeof migrated === 'object' ? migrated : {};
    return withCoverageFields(withBlockFields({
        ...obj,
        header: obj.header ?? defaultHeader,
        fields_style: obj.fields_style ?? defaultFieldsStyle,
        product_card: obj.product_card ?? defaultProductCard,
        totals_style: obj.totals_style ?? defaultTotalsStyle,
        shipping_method: obj.shipping_method ?? defaultShippingMethod,
        coverage: obj.coverage ?? defaultCoverage,
        messages: obj.messages ?? defaultMessages,
        tracking: obj.tracking ?? defaultTracking,
        thank_you: obj.thank_you ?? defaultThankYou,
    }));
}
//# sourceMappingURL=validate.js.map